import { ACCOUNT_CATEGORY } from "@toruslabs/base-controllers";
import OpenLogin from "@toruslabs/openlogin";
import { OpenloginSessionData, UX_MODE } from "@toruslabs/openlogin-utils";
import { Mutex } from "async-mutex";
import log from "loglevel";

import config from "@/config";
import { AccountKey } from "@/utils/enums";
import { getIFrameOriginObject } from "@/utils/helpers";

// type storageKeyType = "local" | "session";

interface IOpenLoginHandler {
  openLoginInstance: OpenLogin;
  sessionId: string | null;
  getKeysInfo(): AccountKey[];
  invalidateSession(): Promise<void>;
}

type OpenloginStateWithAccounts = {
  accounts: Record<string, string>[];
  privKey: string;
  accountType: string;
} & OpenloginSessionData;
class OpenLoginHandler {
  static openLoginHandlerInstance: IOpenLoginHandler;

  private static mutex = new Mutex();

  openLoginInstance: OpenLogin;

  // This constructor is private. Don't call it
  constructor(sessionNamespace = "") {
    const iframeObj = getIFrameOriginObject();
    const namespace = config.isCustomLogin() ? iframeObj.hostname : undefined;
    this.openLoginInstance = new OpenLogin({
      clientId: config.openLoginClientId[config.torusNetwork],
      network: config.torusNetwork,
      whiteLabel: config.openLoginWhiteLabel,
      redirectUrl: `${config.baseRoute}end`,
      replaceUrlOnRedirect: true,
      uxMode: UX_MODE.REDIRECT,
      sessionNamespace: sessionNamespace || namespace,
      storageKey: "local",
      originData:
        config.torusNetwork === "mainnet"
          ? {
              "https://xrpl.tor.us": "MEUCIQCxY24s4by4Ohj5Ehqzy8RDlJ7TCTpVC5qlzIp8-0S5EAIgG1azdVR0FtqDeHeScELtDgyNg26yQ3UlnpTW0MT8gYA",
            }
          : {},
    });
  }

  get sessionId() {
    return this.openLoginInstance.state.sessionId || null;
  }

  get sessionNamespace() {
    return this.openLoginInstance.options.sessionNamespace || "";
  }

  static async getInstance(sessionNamespace?: string, reinitialize = false) {
    const releaseLock = await this.mutex.acquire();
    try {
      if (OpenLoginHandler.openLoginHandlerInstance) {
        if (reinitialize) {
          await OpenLoginHandler.openLoginHandlerInstance.openLoginInstance.init();
        }
        return OpenLoginHandler.openLoginHandlerInstance;
      }
      OpenLoginHandler.openLoginHandlerInstance = new OpenLoginHandler(sessionNamespace);
      if (reinitialize) {
        await OpenLoginHandler.openLoginHandlerInstance.openLoginInstance.init();
      }
      return OpenLoginHandler.openLoginHandlerInstance;
    } finally {
      releaseLock();
    }
  }

  async invalidateSession() {
    try {
      await this.openLoginInstance.logout();
    } catch (error: unknown) {
      log.warn(error);
    }
  }

  getUserInfo() {
    const allInfo = this.openLoginInstance.getUserInfo();
    const userInfo = {
      name: allInfo.name, // first + last name
      profileImage: allInfo.profileImage, // image url
      email: allInfo.email,
      verifier: allInfo.aggregateVerifier, // enum like GOOGLE
      verifierId: allInfo.verifierId, // usually email or facebook id
      verifierParams: { verifier_id: allInfo.verifierId }, // general params
      typeOfLogin: allInfo.typeOfLogin,
    };
    return userInfo;
  }

  getKeysInfo(): AccountKey[] {
    const { state } = this.openLoginInstance;
    // keys
    const keys: AccountKey[] = [];
    if (state.privKey) {
      keys.push({
        privKey: state.privKey.padStart(64, "0"),
        accountType: ACCOUNT_CATEGORY.NORMAL,
      });
    }
    const { accounts } = state as OpenloginStateWithAccounts;

    if (accounts && typeof accounts === "object") {
      Object.values(accounts).forEach((val) => {
        keys.push({
          privKey: val.privKey.padStart(64, "0"),
          accountType: val.accountType,
        });
      });
    }

    return keys;
  }
}
export default OpenLoginHandler;
